/** Watchlist: the add-item box and the live deal table. */

import { catalog$ } from "../data/catalog.ts";
import { ignoreOffer } from "../engine/dedupe.ts";
import { bestDeal$, pokeAll, recomputeAll, rows$ } from "../engine/scheduler.ts";
import { feeRate, netValue, offerKey } from "../engine/scoring.ts";
import { settings$, watchlist$ } from "../state/store.ts";
import type { Offer, RowState, WatchItem } from "../state/types.ts";
import { buyUrl, itemImage } from "./links.ts";
import { append, card, clear, el, on } from "./dom.ts";
import { createTypeahead } from "./typeahead.ts";
import { digitsOnly, groupDigits, money, moneyExact, percent, relativeTime } from "../util/format.ts";

export function addWatchItem(id: number, name: string, type: string, marketValue: number): boolean {
  if (watchlist$.get().some((item) => item.id === id)) return false;
  watchlist$.update((items) => [
    ...items,
    {
      id,
      name,
      type,
      marketValue,
      maxBuyPrice: null,
      minUnits: null,
      intervalSec: null,
      addedAt: Date.now(),
    },
  ]);
  pokeAll();
  return true;
}

export function removeWatchItem(id: number): void {
  watchlist$.update((items) => items.filter((item) => item.id !== id));
}

function patchItem(id: number, patch: Partial<WatchItem>): void {
  watchlist$.update((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  recomputeAll();
}

// ---------------------------------------------------------------- add panel

export function createAddItemPanel(): HTMLElement {
  const feedback = el("p", { class: "tiny mb-0 mt-2 d-none" });

  const typeahead = createTypeahead({
    placeholder: "Search items to watch…",
    onPick: (item) => {
      const added = addWatchItem(item.id, item.name, item.type, item.marketValue);
      feedback.textContent = added
        ? `Watching ${item.name}.`
        : `${item.name} is already on the watchlist.`;
      feedback.className = `tiny mb-0 mt-2 ${added ? "text-success-emphasis" : "text-warning-emphasis"}`;
    },
    disabledReason: () =>
      catalog$.get().items.length === 0 ? "Add a valid API key to load the item catalog…" : null,
  });

  catalog$.subscribe(() => typeahead.refresh(), false);

  const body = el("div", {}, typeahead.root, feedback, createImportExport());
  return card("Add item", body);
}

/**
 * Import/export exists because a watchlist is real work to rebuild, and there
 * is no account to sync it to — the file is the backup.
 */
function createImportExport(): HTMLElement {
  const exportBtn = el("button", {
    type: "button",
    class: "btn btn-sm btn-outline-secondary",
    text: "Export list",
  });

  on(exportBtn, "click", () => {
    const payload = JSON.stringify({ version: 1, items: watchlist$.get() }, null, 2);
    const blob = new Blob([payload], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = el("a", { href: url, download: "deal-sniper-watchlist.json" });
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  });

  const fileInput = el("input", { type: "file", accept: "application/json", class: "d-none" });
  const importBtn = el("button", {
    type: "button",
    class: "btn btn-sm btn-outline-secondary",
    text: "Import list",
  });
  const status = el("span", { class: "tiny text-body-secondary" });

  on(importBtn, "click", () => fileInput.click());

  on(fileInput, "change", () => {
    const file = fileInput.files?.[0];
    if (!file) return;

    void file
      .text()
      .then((text) => {
        const parsed = JSON.parse(text) as { items?: WatchItem[] } | WatchItem[];
        const incoming = Array.isArray(parsed) ? parsed : (parsed.items ?? []);
        const existing = new Set(watchlist$.get().map((item) => item.id));

        const added = incoming.filter(
          (item) => typeof item?.id === "number" && !existing.has(item.id),
        );

        watchlist$.update((items) => [
          ...items,
          ...added.map((item) => ({
            id: item.id,
            name: item.name ?? `Item ${item.id}`,
            type: item.type ?? "",
            marketValue: item.marketValue ?? 0,
            maxBuyPrice: item.maxBuyPrice ?? null,
            minUnits: item.minUnits ?? null,
            intervalSec: item.intervalSec ?? null,
            addedAt: Date.now(),
          })),
        ]);

        status.textContent = `Imported ${added.length} of ${incoming.length}${
          incoming.length !== added.length ? " (rest already watched)" : ""
        }.`;
        pokeAll();
      })
      .catch(() => {
        status.textContent = "Could not read that file.";
      })
      .finally(() => {
        fileInput.value = "";
      });
  });

  return el(
    "div",
    { class: "d-flex align-items-center gap-2 mt-3 flex-wrap" },
    importBtn,
    exportBtn,
    fileInput,
    status,
  );
}

// ------------------------------------------------------------------- table

function sourceBadge(offer: Offer): HTMLElement {
  const isBazaar = offer.source === "bazaar";
  return el("span", {
    class: `badge ${isBazaar ? "text-bg-info" : "text-bg-secondary"} tiny-caps`,
    text: isBazaar ? "bazaar" : "market",
    title: isBazaar ? "From a player bazaar (via weav3r)" : "From the Item Market",
  });
}

function numericCell(
  label: string,
  value: number | null,
  placeholder: string,
  onCommit: (value: number | null) => void,
  title: string,
): HTMLElement {
  const input = el("input", {
    type: "text",
    inputmode: "numeric",
    class: "form-control form-control-sm mono text-end",
    placeholder,
    title,
    value: value === null ? "" : groupDigits(value),
  }) as HTMLInputElement;

  on(input, "input", () => {
    const caretAtEnd = input.selectionStart === input.value.length;
    input.value = groupDigits(input.value);
    if (caretAtEnd) input.setSelectionRange(input.value.length, input.value.length);
  });

  const commit = () => {
    const digits = digitsOnly(input.value);
    onCommit(digits === "" ? null : Number(digits));
  };

  on(input, "change", commit);
  on(input, "blur", commit);

  return el(
    "div",
    { class: "d-flex flex-column" },
    el("span", { class: "control-label", text: label }),
    input,
  );
}

function bonusChips(offer: Offer): HTMLElement | null {
  const bonuses = offer.details?.bonuses ?? [];
  if (bonuses.length === 0) return null;

  return el(
    "span",
    { class: "d-inline-flex flex-wrap gap-1" },
    ...bonuses.map((bonus) =>
      el("span", {
        class: "bonus-chip",
        text: `${bonus.name} ${bonus.value}`,
        title: bonus.description ?? "",
      }),
    ),
  );
}

function renderRow(item: WatchItem, state: RowState, isBest: boolean): HTMLElement {
  const settings = settings$.get();
  const fee = feeRate(settings);
  const net = netValue(item.marketValue, fee);
  const best = state.best;

  const tierClass = state.tier ? `tier-${state.tier}` : "";

  // ---- identity
  const rarity = best?.details?.rarity;
  const identity = el(
    "div",
    { class: "d-flex align-items-center gap-2 min-w-0" },
    el("img", {
      src: itemImage(item.id),
      alt: "",
      class: "item-thumb",
      loading: "lazy",
      width: 46,
      height: 26,
    }),
    el(
      "div",
      { class: "min-w-0" },
      el(
        "div",
        { class: "d-flex align-items-center gap-2 flex-wrap" },
        el("span", {
          class: `fw-semibold text-truncate${rarity ? ` rarity-${rarity}` : ""}`,
          text: item.name,
        }),
        best ? sourceBadge(best) : null,
        state.loading ? el("span", { class: "spinner-border spinner-border-sm text-secondary" }) : null,
      ),
      el(
        "div",
        { class: "tiny text-body-secondary d-flex flex-wrap gap-2" },
        el("span", {
          text: `Mkt avg ${money(item.marketValue)}`,
          title: moneyExact(item.marketValue),
        }),
        fee > 0
          ? el("span", {
              text: `net ${money(net)} (−${Math.round(fee * 100)}%)`,
              title: `What a resale actually pays after fees: ${moneyExact(net)}`,
            })
          : null,
        state.bazaarAverage
          ? el("span", {
              text: `bz avg ${money(state.bazaarAverage)}`,
              title: "Average bazaar asking price, from weav3r",
            })
          : null,
        best && best.quantity > 0 ? el("span", { text: `×${best.quantity}` }) : null,
        best?.sellerName
          ? el("span", { text: best.sellerName, title: `Seller ${best.sellerId}` })
          : null,
        best?.lastChecked
          ? el("span", {
              class: "text-body-tertiary",
              text: `seen ${relativeTime(best.lastChecked * 1000)}`,
              title: "When weav3r last saw this listing",
            })
          : null,
      ),
      bonusChips(best ?? ({} as Offer)),
    ),
  );

  // ---- per-row controls
  const controls = el(
    "div",
    { class: "row-controls d-flex gap-2" },
    numericCell(
      "Max buy",
      item.maxBuyPrice,
      "—",
      (value) => patchItem(item.id, { maxBuyPrice: value }),
      "Absolute ceiling for this row. When set, it replaces the profit-% test: anything at or under is green.",
    ),
    numericCell(
      "Min qty",
      item.minUnits,
      "—",
      (value) => patchItem(item.id, { minUnits: value }),
      "Overrides the global Min units for this row only.",
    ),
  );

  // ---- price + actions
  const priceBlock = el(
    "div",
    { class: "row-price text-end d-flex flex-column align-items-end gap-1" },
    el("div", {
      class: "price-figure",
      text: best ? money(best.price) : "—",
      title: best ? moneyExact(best.price) : "",
    }),
    el("div", {
      class: `tiny mono ${state.margin !== null && state.margin < 0 ? "text-success-emphasis" : "text-body-secondary"}`,
      text: state.margin !== null ? percent(state.margin) : "",
    }),
  );

  const actions = el("div", { class: "d-flex gap-1 justify-content-end mt-1" });

  if (best) {
    append(
      actions,
      el("a", {
        class: "btn btn-sm btn-success",
        href: buyUrl(best),
        target: "_blank",
        rel: "noopener noreferrer",
        text: "BUY",
      }),
    );

    const ignoreBtn = el("button", {
      type: "button",
      class: "btn btn-sm btn-outline-secondary",
      text: "Ignore",
      title: "Never alert on this exact listing again",
    });
    on(ignoreBtn, "click", () => {
      ignoreOffer(offerKey(best));
      recomputeAll();
    });
    append(actions, ignoreBtn);
  }

  const removeBtn = el("button", {
    type: "button",
    class: "btn btn-sm btn-outline-danger",
    text: "×",
    "aria-label": `Stop watching ${item.name}`,
  });
  on(removeBtn, "click", () => removeWatchItem(item.id));
  append(actions, removeBtn);

  append(priceBlock, actions);

  const row = el(
    "div",
    {
      class: `watch-row p-3 border-bottom ${tierClass}${isBest ? " is-best" : ""}`,
      "data-item-id": item.id,
    },
    el("div", { class: "row-identity min-w-0" }, identity),
    controls,
    priceBlock,
  );

  if (state.error) {
    append(row, el("div", { class: "row-error tiny text-danger", text: state.error }));
  }

  return row;
}

export function createWatchlistPanel(): HTMLElement {
  const list = el("div", { class: "list-group list-group-flush" });
  const countBadge = el("span", { class: "badge text-bg-light" });

  const body = el("div", { class: "p-0" }, list);

  function render(): void {
    const items = watchlist$.get();
    const rows = rows$.get();
    const best = bestDeal$.get();

    clear(list);
    countBadge.textContent = `${items.length} ${items.length === 1 ? "entry" : "entries"}`;

    if (items.length === 0) {
      append(
        list,
        el(
          "div",
          { class: "text-center text-body-secondary py-5" },
          el("p", { class: "mb-1 fw-semibold", text: "Watchlist is empty" }),
          el("p", { class: "tiny mb-0", text: "Add an item or a ranked weapon below." }),
        ),
      );
      return;
    }

    // Green deals first, then by how far under net value they sit.
    const order = [...items].sort((a, b) => {
      const rowA = rows.get(a.id);
      const rowB = rows.get(b.id);
      const rank = (tier: string | null | undefined) =>
        tier === "green" ? 0 : tier === "orange" ? 1 : 2;
      const byTier = rank(rowA?.tier) - rank(rowB?.tier);
      if (byTier !== 0) return byTier;
      return (rowA?.margin ?? 1) - (rowB?.margin ?? 1);
    });

    for (const item of order) {
      const state = rows.get(item.id) ?? {
        best: null,
        tier: null,
        margin: null,
        marketBest: null,
        bazaarBest: null,
        bazaarAverage: null,
        error: null,
        lastMarketAt: null,
        lastBazaarAt: null,
        loading: false,
      };
      append(list, renderRow(item, state, best?.itemId === item.id));
    }
  }

  watchlist$.subscribe(render, false);
  rows$.subscribe(render, false);
  settings$.subscribe(render, false);
  bestDeal$.subscribe(render, false);
  render();

  return card("Watchlist", body, countBadge);
}
